import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, updateData } from '../../../services/admin/blogs/blogsServices';
import { Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Swal from 'sweetalert2';
import Spinner from '../../../components/Spinner';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const quillModules = {
    toolbar: [
        [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ script: 'sub' }, { script: 'super' }],
        ['blockquote', 'code-block'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['link'],
        ['clean'],
    ],
};
const quillFormats = [
    'font', 'size', 'header',
    'bold', 'italic', 'underline', 'strike', 'script',
    'blockquote', 'code-block',
    'color', 'background', 'align',
    'list', 'bullet', 'indent', 'link',
];

const EMPTY_FORM = {
    id: '',
    title: '',
    description: '',
    author: '',
    subject: '',
    alt_name: '',
    blog_img: '',
    sub_domain: '',
    approval: 0,
    remarks_history: [],
};

// ── remarks attribution (admin side): always tagged as 'admin' ─────────────
const currentUserName = 'admin';

const PendingBlogs = () => {
    const [dataList, setDataList] = useState([]);
    const [form, setForm] = useState(EMPTY_FORM);

    // 'list' | 'review' — replaces modalType + showModal
    const [view, setView] = useState('list');
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [remarksError, setRemarksError] = useState('');

    const fetchData = useCallback(async () => {
        try {
            const data = await getData({ approval: 0 });
            setDataList(data || []);
        } catch (error) {
            console.error('Failed to fetch blogs:', error);
        }
    }, []);

    useEffect(() => {
        const fetchDataAsync = async () => {
            setLoading(true);

            await fetchData();

            await new Promise((resolve) =>
                setTimeout(resolve, 1000)
            );

            setLoading(false);
        };

        fetchDataAsync();
    }, [fetchData]);

    // ── open inline review view (replaces openModal) ───────────────────────
    const openReview = (data = {}) => {
        let parsedHistory = data.remarks_history || [];
        if (typeof parsedHistory === 'string') {
            try { parsedHistory = JSON.parse(parsedHistory); } catch { parsedHistory = []; }
        }

        setForm({ ...EMPTY_FORM, ...data, remarks_history: parsedHistory });
        setRemarks('');
        setRemarksError('');
        setView('review');
    };

    const closeReview = () => {
        setView('list');
        setForm(EMPTY_FORM);
        setRemarks('');
        setRemarksError('');
    };

    // ── quill ────────────────────────────────────────────────────────────
    const handleDescriptionChange = (value) => {
        setForm((prev) => ({ ...prev, description: value }));
    };

    const handleSubmit = async () => {
        if (!remarks.trim()) {
            setRemarksError('Remarks is required');
            return;
        }
        setRemarksError('');

        try {
            setSubmitting(true);

            const updatedHistory = [
                ...(Array.isArray(form.remarks_history) ? form.remarks_history : []),
                {
                    remark: remarks.trim(),
                    timestamp: new Date().toISOString(),
                    user: currentUserName,
                },
            ];

            const updatedForm = {
                ...form,
                approval: 1,
                remarks_history: JSON.stringify(updatedHistory),
            };

            console.log('Approving Blog:', updatedForm);

            await updateData(updatedForm);

            await fetchData();

            closeReview();

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Blog Approved Successfully!',
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error('Approval Error:', error);

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to approve blog!',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const filteredData = useMemo(() => {
        return dataList
            .filter(
                (blog) =>
                    (blog.title || '')
                        .toLowerCase()
                        .includes(searchText.toLowerCase()) ||
                    (blog.author || '')
                        .toLowerCase()
                        .includes(searchText.toLowerCase()) ||
                    (blog.subject || '')
                        .toLowerCase()
                        .includes(searchText.toLowerCase()) ||
                    (blog.sub_domain || '')
                        .toLowerCase()
                        .includes(searchText.toLowerCase())
            )
            .sort(
                (a, b) =>
                    new Date(b.updatedAt) -
                    new Date(a.updatedAt)
            );
    }, [dataList, searchText]);

    const columns = [
        {
            name: '#',
            selector: (row, index) =>
                (currentPage - 1) * 10 + (index + 1),
            sortable: false,
            width: '70px',
        },
        {
            name: 'Image',
            cell: (row) => (
                <img
                    src={row.blog_img}
                    alt={row.alt_name}
                    width="100"
                    height="60"
                    style={{
                        objectFit: 'cover',
                        borderRadius: '5px',
                    }}
                />
            ),
            sortable: false,
            width: '130px',
        },
        {
            name: 'Title',
            selector: (row) => row.title,
            sortable: true,
            grow: 2,
            wrap: true,
        },
        {
            name: 'Author',
            selector: (row) => row.author,
            sortable: true,
            wrap: true,
        },
        {
            name: 'Subject',
            selector: (row) => row.subject,
            sortable: true,
            wrap: true,
        },
        {
            name: 'Sub Domain',
            selector: (row) => row.sub_domain,
            sortable: true,
            wrap: true,
        },
        {
            name: 'Status',
            cell: () => (
                <span className="badge bg-danger">
                    Pending
                </span>
            ),
            width: '100px',
        },
        {
            name: 'Actions',
            cell: (row) => (
                <Button
                    variant="warning"
                    size="sm"
                    onClick={() => openReview(row)}
                >
                    Approve
                </Button>
            ),
            width: '120px',
        },
    ];

    const isReviewView = view === 'review';

    return (
        <div className="d-flex">
            <Sidebar />

            <div className="main-content w-100">
                <Header />

                {loading ? (
                    <div
                        className="d-flex justify-content-center align-items-center w-100"
                        style={{ minHeight: '300px' }}
                    >
                        <Spinner />
                    </div>
                ) : (
                    <div>
                        <div className="container-fluid p-4">

                            {!isReviewView ? (
                                <>
                                    {/* ════ LIST VIEW ════ */}
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <p className="fs-4 text-dark">
                                                Blogs / Pending Blogs
                                            </p>
                                        </div>
                                    </div>

                                    <DataTable
                                        columns={columns}
                                        data={filteredData}
                                        pagination
                                        highlightOnHover
                                        responsive
                                        striped
                                        paginationPerPage={10}
                                        paginationComponentOptions={{
                                            noRowsPerPage: true,
                                        }}
                                        onChangePage={handlePageChange}
                                        subHeader
                                        subHeaderComponent={
                                            <div>
                                                <input
                                                    className="form-control"
                                                    type="text"
                                                    placeholder="Search"
                                                    value={searchText}
                                                    onChange={handleSearchChange}
                                                />
                                            </div>
                                        }
                                    />
                                </>
                            ) : (
                                <>
                                    {/* ════ REVIEW / APPROVE INLINE VIEW ════ */}
                                    <div className="row mb-3 align-items-center">
                                        <div className="col-12">
                                            <button
                                                className="btn btn-link ps-0 text-decoration-none"
                                                onClick={closeReview}
                                            >
                                                ← Back to pending list
                                            </button>
                                            <p className="fs-4 text-dark mb-0">
                                                Review Blog — {form.title}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="row">
                                        {/* ── col-8: image + description (Quill) ──────────── */}
                                        <div className="col-lg-8 mb-4">
                                            <div className="card shadow-sm">
                                                <div className="card-body">

                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="fs-5 text-dark">
                                                            Blog Image
                                                        </Form.Label>
                                                        <div>
                                                            <img
                                                                src={form.blog_img}
                                                                alt={form.alt_name}
                                                                className="rounded border"
                                                                style={{
                                                                    width: '100%',
                                                                    maxHeight: 320,
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        </div>
                                                    </Form.Group>

                                                    <Form.Group className="mb-2">
                                                        <Form.Label className="fs-5 text-dark">
                                                            Description
                                                            <small className="text-muted ms-1">
                                                                (editable — fix typos before approving)
                                                            </small>
                                                        </Form.Label>
                                                        <ReactQuill
                                                            value={form.description}
                                                            onChange={handleDescriptionChange}
                                                            theme="snow"
                                                            modules={quillModules}
                                                            formats={quillFormats}
                                                            placeholder="Blog content…"
                                                        />
                                                    </Form.Group>

                                                </div>
                                            </div>
                                        </div>

                                        {/* ── col-4: title/author/subject/subdomain/alt name + history ── */}
                                        <div className="col-lg-4">
                                            <div className="card shadow-sm">
                                                <div className="card-body">

                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="text-muted small text-uppercase fw-semibold mb-1">
                                                            Title
                                                        </Form.Label>
                                                        <p className="mb-0">{form.title}</p>
                                                    </Form.Group>

                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="text-muted small text-uppercase fw-semibold mb-1">
                                                            Author
                                                        </Form.Label>
                                                        <p className="mb-0">{form.author}</p>
                                                    </Form.Group>

                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="text-muted small text-uppercase fw-semibold mb-1">
                                                            Subject
                                                        </Form.Label>
                                                        <p className="mb-0">{form.subject}</p>
                                                    </Form.Group>

                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="text-muted small text-uppercase fw-semibold mb-1">
                                                            Sub Domain
                                                        </Form.Label>
                                                        <p className="mb-0">{form.sub_domain}</p>
                                                    </Form.Group>

                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="text-muted small text-uppercase fw-semibold mb-1">
                                                            Alt Name
                                                        </Form.Label>
                                                        <p className="mb-0">{form.alt_name}</p>
                                                    </Form.Group>

                                                    <hr />

                                                    <Form.Label className="text-muted small text-uppercase fw-semibold mb-2">
                                                        Remarks History
                                                    </Form.Label>
                                                    {Array.isArray(form.remarks_history) && form.remarks_history.length > 0 ? (
                                                        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                                            {[...form.remarks_history].reverse().map((item, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    style={{
                                                                        padding: '6px 10px',
                                                                        marginBottom: 6,
                                                                        borderLeft: '3px solid #0d6efd',
                                                                        background: '#f8f9fa',
                                                                        borderRadius: '0 4px 4px 0',
                                                                    }}
                                                                >
                                                                    <div className="d-flex justify-content-between align-items-baseline">
                                                                        <small className="text-muted d-block mb-1">
                                                                            {new Date(item.timestamp).toLocaleString('en-IN', {
                                                                                day: '2-digit', month: 'short', year: 'numeric',
                                                                                hour: '2-digit', minute: '2-digit',
                                                                            })}
                                                                        </small>
                                                                        {item.user && (
                                                                            <span className="badge bg-secondary" style={{ fontSize: 10 }}>
                                                                                {item.user}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <span style={{ fontSize: 14 }}>{item.remark}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted small">No remarks added</span>
                                                    )}

                                                    <hr />

                                                    <Form.Group className="mb-0">
                                                        <Form.Label className="fs-6 text-dark">
                                                            Remarks <span className="text-danger">*</span>
                                                            <small className="text-muted ms-1">
                                                                (will be saved as {currentUserName})
                                                            </small>
                                                        </Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            value={remarks}
                                                            onChange={(e) => {
                                                                setRemarks(e.target.value);
                                                                if (remarksError) setRemarksError('');
                                                            }}
                                                            isInvalid={!!remarksError}
                                                            placeholder="Enter remarks for this approval…"
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {remarksError}
                                                        </Form.Control.Feedback>
                                                    </Form.Group>

                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ── BOTTOM ACTION BAR ──────────────────────────────── */}
                                    <div
                                        className="d-flex justify-content-end gap-2 mt-3 p-3 bg-white border rounded shadow-sm"
                                        style={{ position: 'sticky', bottom: 16 }}
                                    >
                                        <Button
                                            variant="secondary"
                                            onClick={closeReview}
                                            disabled={submitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="success"
                                            onClick={handleSubmit}
                                            disabled={submitting}
                                        >
                                            {submitting ? 'Approving…' : 'Approve Blog'}
                                        </Button>
                                    </div>
                                </>
                            )}

                        </div>

                        <Footer />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PendingBlogs;
